import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive, ArchiveRestore, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
  Columns, ExternalLink, FileSignature, FolderInput, FolderPlus, MoreHorizontal,
  Pencil, Plus, RefreshCw, Search, SlidersHorizontal, Tags, X,
} from 'lucide-react';
import { FieldLabel } from '@documents-ui';
import type { Folder, LegalDocument } from '@platform/shared-types';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { categoryLabel, useDocumentCategories } from './documentCategories';
import MoveDocumentModal from './MoveDocumentModal';
import FolderEditorModal from './FolderEditorModal';
import { folderPalette } from './folderPalette';
import type { DocumentCategory } from '@platform/shared-types';
import { documentsApi } from '../../services/documentsApi';
import SystemDocumentsPanel from './SystemDocumentsPanel';
import type { DocumentDetail } from '../../services/documentsApi';
import {
  activeVersionRow, documentHeadState, envelopeCountText, envelopeCountTitle, formatUtcDay,
  headVersion, isValidSlug, latestPublishedVersion, slugify, tallyEnvelopes,
} from './legalFormat';
import type { EnvelopeTallyState } from './legalFormat';
import { mapPool, POOL } from './mapPool';
import { Button, Explainer, INPUT_CLASS, ModalShell, Tag } from './rubyChrome';

/**
 * LEGAL / AGREEMENTS — the template library.
 *
 * AN AGREEMENT IS A DOCUMENT WITH `kind: 'agreement'`. There is no second
 * engine: the folder tree, the immutable version chain, the block editor, the
 * approval step and the publish gate are the same code Policies runs on, and
 * every row here links into the same `DocumentDetail`. A policy and an
 * agreement diverge only at the last step — a policy is ACCEPTED, an agreement
 * is instantiated into an envelope and SIGNED.
 *
 * THE ENVELOPE COLUMNS ARE REAL NUMBERS, AND A 0 IS ONE OF THEM. 0 means "no
 * envelopes in that state", which is a fact somebody may act on. A count that
 * has not landed or could not be read is NOT zero — it renders as a dash. The
 * 0-vs-dash decision is `envelopeCountText` in ./legalFormat.ts and is
 * unit-tested; nothing here re-implements it.
 *
 * WHY THIS PAGE MAKES N+1 REQUESTS, AND WHY PAGINATION IS NOT COSMETIC.
 * `GET /admin/documents` returns stored document rows only: no
 * `activeVersionNo`, no chain, none of the derived booleans. Status, Version,
 * Effective and Ready-to-send are all derived from the chain, so each row's
 * detail is a `GET /admin/documents/{id}`, and each row's envelope tally is a
 * `GET /admin/envelopes?templateDocumentId=…` on top of that. Two requests per
 * row. THE PAGE THEREFORE EXPANDS ONLY THE ROWS ON THE CURRENT PAGE — a
 * hundred-template library costs two hundred requests if you expand all of it
 * and forty if you expand a page of twenty. Results are cached by documentId
 * for the session, so paging back is free, and Refresh empties the cache
 * because that is the point of pressing it.
 *
 * ARCHIVED IS A VIEW OF THE SAME LIST, NOT A SECOND ONE. `GET /admin/documents`
 * returns retired documents alongside active ones unless `?status=` narrows it,
 * so this page reads once and splits on `status` — which is also why the
 * Archive tab's count is exact rather than a second request's guess. Archiving
 * is `POST .../retire`; restoring is `PATCH { status: 'active' }`, the one
 * status value that route accepts.
 */

/* ── columns ────────────────────────────────────────────────────────────── */

/**
 * THE TABLE IS DEFINED AS DATA, and the header, the body, the column picker and
 * the persisted widths all read this one list. Adding a column is one entry
 * here plus one `cell` branch — not four edits that can disagree.
 *
 * `min` exists because a resizable column with no floor can be dragged to zero
 * width, at which point its header is gone and there is nothing left to grab to
 * get it back.
 */
interface ColumnDef {
  id: string;
  label: string;
  width: number;
  min: number;
  /** Right-aligned numerics read better against their own edge. */
  align?: 'right';
  /** Never hidden — a row with no title is not identifiable. */
  locked?: boolean;
  /** Sortable only where the value is on the LIST row — see `sorted` below. */
  sortable?: boolean;
}

const COLUMNS: readonly ColumnDef[] = [
  { id: 'title', label: 'Template', width: 280, min: 170, locked: true, sortable: true },
  { id: 'folder', label: 'Folder', width: 150, min: 90, sortable: true },
  { id: 'category', label: 'Category', width: 165, min: 100, sortable: true },
  { id: 'status', label: 'Status', width: 120, min: 90 },
  { id: 'version', label: 'Version', width: 95, min: 70 },
  { id: 'effective', label: 'Effective', width: 120, min: 95 },
  { id: 'ready', label: 'Ready to send', width: 135, min: 105 },
  { id: 'envelopes', label: 'Envelopes', width: 105, min: 80, align: 'right' },
  { id: 'awaiting', label: 'Awaiting signature', width: 155, min: 110, align: 'right' },
  { id: 'executed', label: 'Executed', width: 105, min: 80, align: 'right' },
];

const ACTIONS_WIDTH = 56;
const PAGE_SIZES = [10, 20, 50, 100] as const;

/**
 * Column widths and hidden columns, per browser.
 *
 * A layout an operator dragged into shape is worthless if it resets on every
 * navigation, and this is exactly the kind of preference that must never leave
 * the machine it was set on — it is a display choice, not account state. Reads
 * are guarded: a private window, cleared site data or a corrupt value all have
 * to degrade to the defaults rather than throw at module scope.
 */
const PREFS_KEY = 'legal.agreements.table.v1';

interface TablePrefs {
  widths: Record<string, number>;
  hidden: string[];
  pageSize: number;
}

const DEFAULT_PREFS: TablePrefs = { widths: {}, hidden: [], pageSize: 20 };

function readPrefs(): TablePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return {
      widths: (parsed?.widths && typeof parsed.widths === 'object') ? parsed.widths : {},
      hidden: Array.isArray(parsed?.hidden) ? parsed.hidden.filter((x: unknown) => typeof x === 'string') : [],
      pageSize: PAGE_SIZES.includes(parsed?.pageSize) ? parsed.pageSize : DEFAULT_PREFS.pageSize,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function writePrefs(prefs: TablePrefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* storage unavailable */ }
}

/* ── small pieces ───────────────────────────────────────────────────────── */

interface TreeNode {
  folder: Folder;
  children: TreeNode[];
}

function buildTree(folders: readonly Folder[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  folders.forEach((f) => byId.set(f.folderId, { folder: f, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    const parentId = node.folder.parentFolderId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node); else roots.push(node);
  });
  return roots;
}

function StatTile({
  value, label, note, tone,
}: { value: string; label: string; note?: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className={`text-2xl font-bold tracking-tight ${tone || 'text-stone-900'}`}>{value}</div>
      <div className="mt-1 text-[11.5px] text-stone-500">{label}</div>
      {note && <div className="mt-0.5 text-[10.5px] text-stone-400">{note}</div>}
    </div>
  );
}

/**
 * One envelope-count cell.
 *
 * The whole point of this component is that it never renders a number it does
 * not have. `envelopeCountText` returns `0` for a real zero and a dash for
 * everything else, and the dash carries a `title` saying which kind of "we do
 * not know" it is — still loading, or the read failed — because those are
 * different problems for the reader even though they are the same blank.
 */
function CountCell({
  state, field, tone,
}: {
  state: EnvelopeTallyState | undefined;
  field: 'total' | 'awaiting' | 'executed';
  tone?: string;
}) {
  const text = envelopeCountText(state, field);
  const unknown = text === '—';
  const title = envelopeCountTitle(state);
  return (
    <span className={unknown ? 'text-stone-400' : (tone || 'text-stone-600')} title={title || undefined}>
      {text}
    </span>
  );
}

/** A shimmer block sized like the value it stands in for. */
const Bar = ({ w = 'w-full' }: { w?: string }) => (
  <div className={`h-3 animate-pulse rounded bg-stone-200 ${w}`} />
);

/* ── the page ───────────────────────────────────────────────────────────── */

export default function AgreementsList() {
  const navigate = useNavigate();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  /**
   * Caches keyed by documentId, MERGED rather than replaced as pages are
   * expanded. A page already visited must not go back to skeletons when the
   * operator pages back onto it.
   */
  const [details, setDetails] = useState<Record<string, DocumentDetail>>({});
  /**
   * documentId → whether its envelope counts are known, and if not, why not.
   * A MISSING key is "not asked yet", which reads as unknown exactly like
   * `loading` does. Nothing in here is ever a bare number: a number with no
   * state attached could not tell 0 apart from a failed fetch.
   */
  const [envCounts, setEnvCounts] = useState<Record<string, EnvelopeTallyState>>({});
  const [loading, setLoading] = useState(true);
  const [expanding, setExpanding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Bumped whenever the caches are emptied. The expand effect keys off the
   * page's row ids, and those do not change when Refresh clears the caches —
   * without this the page would sit on skeletons that nothing ever re-fetches.
   */
  const [cacheEpoch, setCacheEpoch] = useState(0);

  const [view, setView] = useState<'active' | 'archived'>('active');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'title', dir: 'asc' });
  const [page, setPage] = useState(1);

  const [prefs, setPrefs] = useState<TablePrefs>(readPrefs);
  const [columnPicker, setColumnPicker] = useState(false);

  const [docModal, setDocModal] = useState(false);
  const [folderModal, setFolderModal] = useState<{ folder: Folder | null } | null>(null);
  /** The document whose filing is being changed, and which half is being asked. */
  const [moving, setMoving] = useState<{ doc: LegalDocument; mode: 'folder' | 'category' } | null>(null);
  const [archiving, setArchiving] = useState<LegalDocument | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  /** Which row's action menu is open, and where to draw it. */
  const [menu, setMenu] = useState<{ doc: LegalDocument; x: number; y: number } | null>(null);

  useEffect(() => { writePrefs(prefs); }, [prefs]);

  /* ── data ─────────────────────────────────────────────────────────────── */

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, d] = await Promise.all([documentsApi.listFolders(), documentsApi.listDocuments()]);
      setFolders(f.folders || []);
      // The service has no `kind` query parameter — the split is ours to make.
      // Retired rows come back too and are kept: the Archive tab is a view of
      // this same list, not a second request.
      setDocuments((d.documents || []).filter((doc) => doc.kind === 'agreement'));
    } catch (e: any) {
      setError(e?.message || 'documents_unavailable');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    // Emptied, not merged: Refresh exists to discard what is cached, and a
    // merge would show the operator the same stale chain they pressed it over.
    setDetails({});
    setEnvCounts({});
    setCacheEpoch((n) => n + 1);
    await load();
  }, [load]);

  useEffect(() => { void load(); }, [load]);

  const tree = useMemo(() => buildTree(folders), [folders]);
  const folderById = useMemo(
    () => new Map(folders.map((f) => [f.folderId, f] as const)),
    [folders],
  );

  // Agreement categories only — this page never offers "Cookie policy".
  const { options: categoryChoices } = useDocumentCategories('agreement');

  const active = useMemo(() => documents.filter((d) => d.status !== 'retired'), [documents]);
  const archived = useMemo(() => documents.filter((d) => d.status === 'retired'), [documents]);
  const inView = view === 'archived' ? archived : active;

  const docsInFolder = useMemo(() => {
    const m: Record<string, number> = {};
    inView.forEach((d) => { m[d.folderId] = (m[d.folderId] || 0) + 1; });
    return m;
  }, [inView]);

  /**
   * Three independent filters answering three different questions: the folder
   * tree is "where is this filed", the category dropdown is "where is this
   * offered", and search is "what is it called". None implies the others, so
   * they narrow together.
   */
  const filtered = useMemo(() => {
    let list = selectedFolderId ? inView.filter((d) => d.folderId === selectedFolderId) : inView;
    if (categoryFilter) {
      list = categoryFilter === '__none__'
        ? list.filter((d) => !d.category)
        : list.filter((d) => d.category === categoryFilter);
    }
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((d) => (
        d.title.toLowerCase().includes(term)
        || d.slug.toLowerCase().includes(term)
        || categoryLabel(d.category).toLowerCase().includes(term)
        || (folderById.get(d.folderId)?.name.toLowerCase().includes(term) ?? false)
      ));
    }
    return list;
  }, [inView, selectedFolderId, categoryFilter, search, folderById]);

  /**
   * SORTING IS OFFERED ONLY ON FIELDS THE LIST ROW CARRIES. Version, Effective
   * and the envelope counts live on the per-row detail, which exists for the
   * CURRENT PAGE only — sorting the library by a value three quarters of it has
   * not fetched would reorder the rows under the operator as each page landed,
   * and put the wrong twenty rows on page one.
   */
  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const value = (d: LegalDocument): string => {
      if (sort.key === 'folder') return folderById.get(d.folderId)?.name ?? '';
      if (sort.key === 'category') return d.category ? categoryLabel(d.category) : '￿';
      return d.title;
    };
    return [...filtered].sort((a, b) => value(a).localeCompare(value(b)) * dir);
  }, [filtered, sort, folderById]);

  const pageSize = prefs.pageSize;
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageRows = useMemo(
    () => sorted.slice(pageStart, pageStart + pageSize),
    [sorted, pageStart, pageSize],
  );

  // Any change to what is being listed sends the reader back to page 1 —
  // staying on page 7 of a two-page result shows an empty table.
  useEffect(() => { setPage(1); }, [view, selectedFolderId, categoryFilter, search, pageSize, sort]);

  /**
   * EXPAND ONLY THE CURRENT PAGE, and only the rows not already expanded.
   *
   * Two requests per row (chain + envelope tally), both through the same
   * bounded pool — a request per row fired unbounded opens a socket per
   * template and the browser queues them anyway. Each is caught INDIVIDUALLY:
   * one rejection escaping into a `Promise.all` would lose every count
   * including the ones that succeeded.
   */
  const pageKey = pageRows.map((d) => d.documentId).join(',');
  useEffect(() => {
    const wanted = pageRows.filter((d) => !details[d.documentId] || !envCounts[d.documentId]);
    if (!wanted.length) { setExpanding(false); return; }

    let alive = true;
    setExpanding(true);
    setEnvCounts((prev) => {
      const next = { ...prev };
      wanted.forEach((d) => { if (!next[d.documentId]) next[d.documentId] = { kind: 'loading' }; });
      return next;
    });

    void (async () => {
      const chains = await mapPool(wanted, POOL, async (doc) => {
        try { return await documentsApi.getDocument(doc.documentId); } catch { return null; }
      });
      if (!alive) return;
      setDetails((prev) => {
        const next = { ...prev };
        chains.forEach((r) => { if (r) next[r.document.documentId] = r; });
        return next;
      });

      // Sequential on purpose: two pools of six against one host would double
      // the concurrency this page was bounded to in the first place.
      const tallies = await mapPool<LegalDocument, EnvelopeTallyState>(wanted, POOL, async (doc) => {
        try {
          const res = await documentsApi.listEnvelopes({ templateDocumentId: doc.documentId });
          return { kind: 'ready', tally: tallyEnvelopes(res.envelopes || []) };
        } catch (e: any) {
          return { kind: 'error', error: e?.message || 'envelope_list_failed' };
        }
      });
      if (!alive) return;
      setEnvCounts((prev) => {
        const next = { ...prev };
        wanted.forEach((d, i) => { next[d.documentId] = tallies[i]; });
        return next;
      });
      setExpanding(false);
    })();

    return () => { alive = false; };
    // `pageKey` is the identity of the page's rows; `pageRows` itself is a new
    // array on every render and would re-fire this forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, cacheEpoch]);

  const stats = useMemo(() => {
    const list = Object.values(details).filter((d) => d.document.kind === 'agreement');
    // "Ready to send" is the question envelope creation asks: is there a
    // PUBLISHED version this envelope can name? A scheduled version counts —
    // it is published, it just has not taken effect yet.
    const ready = list.filter(
      (d) => d.document.status === 'active' && latestPublishedVersion(d.versions) !== null,
    ).length;
    const awaiting = list.filter((d) => headVersion(d.versions)?.status === 'in_review').length;
    return { ready, awaiting, expanded: list.length };
  }, [details]);

  /* ── row actions ──────────────────────────────────────────────────────── */

  const hrefFor = (doc: LegalDocument) => `/legal/agreements/${encodeURIComponent(doc.documentId)}`;

  const openRow = (doc: LegalDocument, newTab: boolean) => {
    const href = hrefFor(doc);
    // `noopener` on every one of these: the opened tab must never get a handle
    // on this one through `window.opener`.
    if (newTab) window.open(href, '_blank', 'noopener,noreferrer');
    else navigate(href);
  };

  const restore = async (doc: LegalDocument) => {
    setRestoring(doc.documentId);
    try {
      await documentsApi.restoreDocument(doc.documentId);
      toastService.success(`${doc.title} restored`);
      // refresh, not load: the cached chain for this row still carries the
      // document as it was BEFORE the change, and the stat tiles read it.
      await refresh();
    } catch (e: any) {
      toastService.error(e?.message || 'document_restore_failed');
    } finally {
      setRestoring(null);
    }
  };

  /* ── column sizing ────────────────────────────────────────────────────── */

  const visibleColumns = COLUMNS.filter((c) => c.locked || !prefs.hidden.includes(c.id));
  const widthOf = (c: ColumnDef) => Math.max(c.min, prefs.widths[c.id] ?? c.width);

  const dragging = useRef<{ id: string; startX: number; startWidth: number; min: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragging.current;
      if (!d) return;
      const next = Math.max(d.min, d.startWidth + (e.clientX - d.startX));
      setPrefs((p) => ({ ...p, widths: { ...p.widths, [d.id]: next } }));
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startResize = (e: ReactMouseEvent, c: ColumnDef) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = { id: c.id, startX: e.clientX, startWidth: widthOf(c), min: c.min };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const toggleSort = (c: ColumnDef) => {
    if (!c.sortable) return;
    setSort((s) => (s.key === c.id ? { key: c.id, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.id, dir: 'asc' }));
  };

  /* ── cells ────────────────────────────────────────────────────────────── */

  const renderCell = (columnId: string, doc: LegalDocument) => {
    const detail = details[doc.documentId];
    const versions = detail?.versions ?? [];
    const retired = doc.status === 'retired';
    const counts = envCounts[doc.documentId];

    switch (columnId) {
      case 'title': {
        const p = folderPalette(folderById.get(doc.folderId)?.color);
        return (
          <div className="flex items-center gap-2.5 overflow-hidden">
            <span className={`h-8 w-1 shrink-0 rounded-full ${p.dot}`} aria-hidden />
            <div className="min-w-0">
              <div className={`truncate text-[13px] font-medium ${retired ? 'text-stone-400' : 'text-stone-900'}`}>
                {doc.title}
              </div>
              <div className="truncate text-[11px] text-stone-400">/legal/{doc.slug}</div>
            </div>
          </div>
        );
      }
      case 'folder': {
        const folder = folderById.get(doc.folderId);
        const p = folderPalette(folder?.color);
        return (
          <span
            className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-md border px-2 py-1 text-[11.5px] ${p.chip}`}
            title={folder?.name ?? doc.folderId}
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${p.dot}`} aria-hidden />
            <span className="truncate">{folder?.name ?? 'Unfiled'}</span>
          </span>
        );
      }
      // Where it is OFFERED. Uncategorised is called out rather than left blank
      // — it means the template reaches no picker at all, which is easy to do
      // by accident.
      case 'category':
        return doc.category
          ? (
            <span className="inline-flex max-w-full truncate rounded-md border border-stone-200 bg-stone-50 px-2 py-1 text-[11.5px] text-stone-700" title={categoryLabel(doc.category)}>
              {categoryLabel(doc.category)}
            </span>
          )
          : <span className="text-[11.5px] italic text-amber-600">Uncategorised</span>;
      case 'status': {
        if (!detail) return <RubyGemMark size={12} drawing />;
        const state = documentHeadState(doc.status, versions);
        return <Tag tone={state.tone}>{state.label}</Tag>;
      }
      case 'version':
        if (!detail) return <RubyGemMark size={12} drawing />;
        return (
          <span className="text-[13px] text-stone-600">
            {detail.activeVersionNo ? `v${detail.activeVersionNo}` : '—'}
          </span>
        );
      case 'effective': {
        if (!detail) return <RubyGemMark size={12} drawing />;
        const row = activeVersionRow(versions, detail.activeVersionNo);
        return <span className="text-[13px] text-stone-600">{formatUtcDay(row?.effectiveAt)}</span>;
      }
      case 'ready': {
        if (!detail) return <RubyGemMark size={12} drawing />;
        if (retired) return <span className="text-[13px] text-stone-400">archived</span>;
        const publishable = latestPublishedVersion(versions);
        return publishable
          ? <Tag tone="published">v{publishable.versionNo} ready</Tag>
          : <span className="text-[13px] text-stone-500">not published</span>;
      }
      // Real counts. 0 is an answer and renders as 0; a count that has not
      // landed or could not be read renders as a dash. See envelopeCountText.
      case 'envelopes':
        return <span className="text-[13px]"><CountCell state={counts} field="total" /></span>;
      case 'awaiting':
        return <span className="text-[13px] font-semibold"><CountCell state={counts} field="awaiting" tone="text-amber-600" /></span>;
      case 'executed':
        return <span className="text-[13px]"><CountCell state={counts} field="executed" tone="text-emerald-600" /></span>;
      default:
        return null;
    }
  };

  /* ── folder rail ──────────────────────────────────────────────────────── */

  const renderNode = (node: TreeNode, depth: number): JSX.Element => {
    const { folder, children } = node;
    const on = selectedFolderId === folder.folderId;
    const isCollapsed = !!collapsed[folder.folderId];
    const p = folderPalette(folder.color);
    return (
      <div key={folder.folderId}>
        <div
          className={`group flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 text-[13px] transition-colors ${
            on ? `${p.selected} font-semibold` : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
          }`}
          style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => setSelectedFolderId(folder.folderId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') setSelectedFolderId(folder.folderId); }}
        >
          {children.length > 0 ? (
            <button
              type="button"
              aria-label={isCollapsed ? 'Expand folder' : 'Collapse folder'}
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed((c) => ({ ...c, [folder.folderId]: !c[folder.folderId] }));
              }}
              className="text-stone-400 hover:text-stone-700"
            >
              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <span className="w-3.5" />
          )}
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${p.dot}`} aria-hidden />
          <span className="truncate">{folder.name}</span>
          <button
            type="button"
            aria-label={`Edit ${folder.name}`}
            title="Rename or recolour"
            onClick={(e) => { e.stopPropagation(); setFolderModal({ folder }); }}
            className="ml-auto hidden h-7 w-7 shrink-0 items-center justify-center rounded-md text-stone-400 hover:bg-white hover:text-admin-primary group-hover:flex"
          >
            <Pencil size={13} />
          </button>
          <span className="ml-auto text-[10px] text-stone-400 group-hover:hidden">
            {docsInFolder[folder.folderId] || 0}
          </span>
        </div>
        {!isCollapsed && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  /* ── render ───────────────────────────────────────────────────────────── */

  const totalWidth = visibleColumns.reduce((sum, c) => sum + widthOf(c), 0) + ACTIONS_WIDTH;
  const showingFrom = sorted.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + pageSize, sorted.length);
  const filtersOn = !!(search.trim() || categoryFilter || selectedFolderId);

  return (
    <StaffPage
      section="Legal"
      icon={FileSignature}
      title="Agreements"
      subtitle="Templates with merge fields. Each envelope names its exact template version."
      tabs={<LegalNav />}
      actions={(
        <>
          <Button onClick={() => void refresh()} disabled={loading}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          {/* Authoring a document and SENDING one are different jobs — this
              list is the former, and the send flow is one click away rather
              than buried in another section. */}
          <Button onClick={() => navigate('/legal/agreements/send')}>
            Send agreement
          </Button>
          <Button
            variant="primary"
            onClick={() => setDocModal(true)}
            disabled={folders.length === 0}
          >
            <Plus className="h-4 w-4" />
            New agreement
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Could not reach documents-service: <b>{error}</b>
          </div>
        )}

        {/* Built-in master legal set (additive, 2026-10-09). The library below is unchanged. */}
        <SystemDocumentsPanel kind="agreement" documents={documents} loading={loading} detailBase="/legal/agreements" />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile value={String(active.length)} label="Active templates" />
          <StatTile
            value={String(archived.length)}
            label="Archived"
            tone={archived.length > 0 ? 'text-stone-500' : 'text-stone-900'}
          />
          {/* Both of these are derived from the version chain, which exists only
              for rows that have been expanded. The note says so rather than
              letting a number imply it counted the whole library. */}
          <StatTile
            value={String(stats.ready)}
            label="Ready to send"
            note={`across ${stats.expanded} expanded row${stats.expanded === 1 ? '' : 's'}`}
            tone={stats.ready > 0 ? 'text-emerald-600' : 'text-stone-900'}
          />
          <StatTile
            value={String(stats.awaiting)}
            label="Awaiting approval"
            note={`across ${stats.expanded} expanded row${stats.expanded === 1 ? '' : 's'}`}
            tone="text-amber-600"
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[248px_1fr]">
          {/* folder rail — the same folders Policies uses; a folder holds both kinds. */}
          <div className="h-fit rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-stone-200 px-4 py-3">
              <h3 className="text-[12px] font-semibold uppercase tracking-wider text-stone-500">Folders</h3>
              <button
                type="button"
                onClick={() => setFolderModal({ folder: null })}
                title="New folder"
                aria-label="New folder"
                className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-admin-primary"
              >
                <FolderPlus size={16} />
              </button>
            </div>
            <div className="p-2">
              <div
                className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 text-[13px] transition-colors ${
                  selectedFolderId === null
                    ? 'bg-stone-100 font-semibold text-stone-900'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                }`}
                onClick={() => setSelectedFolderId(null)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelectedFolderId(null); }}
              >
                <span className="w-3.5" />
                <span>{view === 'archived' ? 'All archived' : 'All agreements'}</span>
                <span className="ml-auto text-[10px] text-stone-400">{inView.length}</span>
              </div>
              {tree.map((n) => renderNode(n, 0))}
              {folders.length === 0 && !loading && (
                <div className="px-2 py-3">
                  <p className="text-[12px] text-stone-500">
                    No folders yet. A document must live in a folder, and folders are shared with
                    Policies.
                  </p>
                  <div className="mt-2">
                    <Button onClick={() => setFolderModal({ folder: null })}>
                      <FolderPlus className="h-4 w-4" />
                      New folder
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <p className="border-t border-stone-200 px-4 py-3 text-[11px] leading-relaxed text-stone-500">
              A folder is how you file a document. Colour it however you like — nothing in the
              product keys on a folder, so moving or recolouring one is always safe.
            </p>
          </div>

          {/* template table */}
          <div className="min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 px-3 py-2.5">
              {/* Active / Archive — moved down from the header, whose tab strip
                  is now the Legal section nav shared by every /legal page. */}
              <div role="tablist" aria-label="Agreement view" className="flex shrink-0 rounded-lg border border-stone-200 bg-stone-50 p-0.5">
                {([
                  { id: 'active', label: 'Active', count: active.length },
                  { id: 'archived', label: 'Archive', count: archived.length },
                ] as const).map((t) => {
                  const on = view === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      onClick={() => setView(t.id)}
                      className={`flex min-h-[44px] items-center gap-1.5 rounded-md px-3 text-[12.5px] font-semibold transition ${
                        on ? 'bg-white text-admin-primary shadow-sm' : 'text-stone-500 hover:text-stone-800'
                      }`}
                    >
                      {t.label}
                      <span className={`rounded-full px-1.5 text-[10.5px] ${on ? 'bg-rose-50 text-admin-primary' : 'bg-stone-200 text-stone-600'}`}>
                        {t.count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="relative min-w-[200px] flex-1">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search title, slug, folder or category…"
                  aria-label="Search agreements"
                  className={`${INPUT_CLASS} !pl-9 !pr-9`}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-700"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* The other axis. The folder rail beside it says where a document
                  is FILED; this says where it is OFFERED. */}
              <div className="w-[210px]">
                <SearchableDropdown
                  options={[
                    { value: '__all__', label: 'All categories' },
                    { value: '__none__', label: 'Uncategorised', subtitle: 'Offered nowhere until a category is set' },
                    ...categoryChoices,
                  ]}
                  value={categoryFilter ?? '__all__'}
                  onChange={(v) => setCategoryFilter(!v || v === '__all__' ? null : v)}
                  placeholder="All categories"
                />
              </div>

              <div className="relative">
                <Button onClick={() => setColumnPicker((v) => !v)}>
                  <Columns className="h-4 w-4" />
                  Columns
                </Button>
                {columnPicker && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setColumnPicker(false)} />
                    <div className="absolute right-0 z-40 mt-1 w-[248px] rounded-xl border border-stone-200 bg-white p-2 shadow-xl">
                      <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                        Show columns
                      </p>
                      {COLUMNS.map((c) => {
                        const shown = c.locked || !prefs.hidden.includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex min-h-[36px] items-center gap-2.5 rounded-lg px-2 text-[12.5px] ${
                              c.locked ? 'text-stone-400' : 'cursor-pointer text-stone-700 hover:bg-stone-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={shown}
                              disabled={c.locked}
                              onChange={() => setPrefs((p) => ({
                                ...p,
                                hidden: shown ? [...p.hidden, c.id] : p.hidden.filter((x) => x !== c.id),
                              }))}
                              className="h-4 w-4 accent-[#DC143C]"
                            />
                            {c.label}
                            {c.locked && <span className="ml-auto text-[10px]">always</span>}
                          </label>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setPrefs((p) => ({ ...p, hidden: [], widths: {} }))}
                        className="mt-1 flex min-h-[36px] w-full items-center gap-2 rounded-lg px-2 text-[12.5px] text-stone-600 hover:bg-stone-50"
                      >
                        <SlidersHorizontal size={14} />
                        Reset columns and widths
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {filtersOn && (
              <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 bg-stone-50 px-3 py-2 text-[11.5px] text-stone-600">
                <span>
                  {sorted.length} of {inView.length} {view === 'archived' ? 'archived' : 'active'} shown
                </span>
                <button
                  type="button"
                  onClick={() => { setSearch(''); setCategoryFilter(null); setSelectedFolderId(null); }}
                  className="rounded-md px-2 py-1 font-medium text-admin-primary hover:bg-white"
                >
                  Clear filters
                </button>
              </div>
            )}

            {/* Horizontal scroll lives HERE, on the table's own container. Every
                cell is nowrap and every column has an explicit width, so a long
                title widens its column instead of wrapping a row to three lines
                — and the page body never scrolls sideways. */}
            <div className="relative overflow-x-auto">
              {/* Over the skeleton rows -- a sibling of the table, never inside tbody. */}
              {loading && <RubyLoader variant="overlay" size={40} />}
              <table className="border-collapse" style={{ width: totalWidth, minWidth: '100%', tableLayout: 'fixed' }}>
                <colgroup>
                  {visibleColumns.map((c) => <col key={c.id} style={{ width: widthOf(c) }} />)}
                  <col style={{ width: ACTIONS_WIDTH }} />
                </colgroup>
                <thead>
                  <tr className="bg-stone-50">
                    {visibleColumns.map((c) => {
                      const sorting = sort.key === c.id;
                      return (
                        <th
                          key={c.id}
                          scope="col"
                          className="relative select-none whitespace-nowrap border-b border-stone-200 px-3 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wider text-stone-500"
                        >
                          <button
                            type="button"
                            onClick={() => toggleSort(c)}
                            title={c.sortable ? `Sort by ${c.label}` : undefined}
                            className={`flex w-full items-center gap-1 truncate uppercase tracking-wider ${
                              c.align === 'right' ? 'justify-end' : ''
                            } ${c.sortable ? 'cursor-pointer hover:text-stone-800' : 'cursor-default'}`}
                          >
                            <span className="truncate">{c.label}</span>
                            {sorting && (
                              <ChevronDown
                                className={`h-3 w-3 shrink-0 transition-transform ${sort.dir === 'desc' ? '' : 'rotate-180'}`}
                              />
                            )}
                          </button>
                          {/* Drag to resize. `-mr-px` puts the grab strip over
                              the border line itself, which is where a reader
                              aims for it. */}
                          <span
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={`Resize ${c.label}`}
                            onMouseDown={(e) => startResize(e, c)}
                            onDoubleClick={() => setPrefs((p) => {
                              const widths = { ...p.widths };
                              delete widths[c.id];
                              return { ...p, widths };
                            })}
                            title="Drag to resize · double-click to reset"
                            className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-admin-primary/40"
                          />
                        </th>
                      );
                    })}
                    <th scope="col" className="border-b border-stone-200 px-2 py-2.5" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {loading && Array.from({ length: Math.min(pageSize, 8) }).map((_, i) => (
                    <tr key={`skeleton-${i}`}>
                      {visibleColumns.map((c) => (
                        <td key={c.id} className="px-3 py-3.5">
                          <Bar w={c.id === 'title' ? 'w-4/5' : 'w-2/3'} />
                        </td>
                      ))}
                      <td className="px-2 py-3.5"><Bar w="w-5" /></td>
                    </tr>
                  ))}

                  {!loading && pageRows.length === 0 && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="px-3 py-10 text-center text-[13px] text-stone-500">
                        {view === 'archived' && archived.length === 0
                          ? 'Nothing archived. Archiving a template withdraws it — no new envelope can name it — while everything already signed stands.'
                          : inView.length === 0
                            ? 'No agreement templates yet. A new agreement starts as an empty v1 draft, exactly like a policy.'
                            : 'No agreements match these filters.'}
                      </td>
                    </tr>
                  )}

                  {!loading && pageRows.map((doc) => (
                    <tr
                      key={doc.documentId}
                      onClick={(e) => openRow(doc, e.metaKey || e.ctrlKey)}
                      onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); openRow(doc, true); } }}
                      className="cursor-pointer hover:bg-stone-50"
                    >
                      {visibleColumns.map((c) => (
                        <td
                          key={c.id}
                          className={`overflow-hidden whitespace-nowrap px-3 py-3 align-middle ${
                            c.align === 'right' ? 'text-right' : ''
                          }`}
                        >
                          {renderCell(c.id, doc)}
                        </td>
                      ))}
                      {/* `stopPropagation` — the row itself opens the template,
                          and every item in this menu is a different intent. */}
                      <td className="px-2 py-3 text-right align-middle">
                        <button
                          type="button"
                          aria-label={`Actions for ${doc.title}`}
                          aria-haspopup="menu"
                          onClick={(e) => {
                            e.stopPropagation();
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setMenu((m) => (m?.doc.documentId === doc.documentId
                              ? null
                              : { doc, x: r.right, y: r.bottom }));
                          }}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-200/70 hover:text-stone-800"
                        >
                          {restoring === doc.documentId
                            ? <RubyGemMark size={16} drawing color="currentColor" />
                            : <MoreHorizontal size={16} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* pagination */}
            <div className="flex flex-wrap items-center gap-3 border-t border-stone-200 bg-stone-50 px-3 py-2.5">
              <span className="text-[11.5px] text-stone-600">
                {sorted.length === 0
                  ? 'No rows'
                  : <>Showing <b className="text-stone-800">{showingFrom}–{showingTo}</b> of <b className="text-stone-800">{sorted.length}</b></>}
                {expanding && (
                  <span className="ml-2 inline-flex items-center gap-1 text-stone-400">
                    <RubyGemMark size={12} drawing />
                    expanding this page…
                  </span>
                )}
              </span>

              <label className="flex items-center gap-1.5 text-[11.5px] text-stone-600">
                Rows
                <select
                  value={pageSize}
                  onChange={(e) => setPrefs((p) => ({ ...p, pageSize: Number(e.target.value) }))}
                  className="min-h-[36px] rounded-lg border border-stone-300 bg-white px-2 text-[12px] text-stone-800 focus:border-admin-primary focus:outline-none"
                >
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>

              <div className="ml-auto flex items-center gap-1">
                <PageButton
                  label="First page"
                  disabled={safePage === 1}
                  onClick={() => setPage(1)}
                >
                  <ChevronsLeft size={15} />
                </PageButton>
                <PageButton
                  label="Previous page"
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronRight size={15} className="rotate-180" />
                </PageButton>
                <span className="px-2 text-[11.5px] text-stone-600">
                  Page <b className="text-stone-800">{safePage}</b> of {pageCount}
                </span>
                <PageButton
                  label="Next page"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  <ChevronRight size={15} />
                </PageButton>
                <PageButton
                  label="Last page"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage(pageCount)}
                >
                  <ChevronsRight size={15} />
                </PageButton>
              </div>
            </div>
          </div>
        </div>

        <Explainer summary={(
          <>
            <b>0 means zero envelopes. A dash means the count is not known.</b> Hover a dash for
            the reason.
          </>
        )}
        >
          <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-[132px_1fr]">
            <dt className="font-semibold text-stone-800">Envelopes</dt>
            <dd>Every envelope created from this template.</dd>
            <dt className="font-semibold text-stone-800">Awaiting signature</dt>
            <dd>Sent or partially signed. A draft envelope is not awaiting anyone — it was never sent.</dd>
            <dt className="font-semibold text-stone-800">Executed</dt>
            <dd>Fully signed. Terminal: never unwound, only superseded by another agreement.</dd>
            <dt className="font-semibold text-stone-800">Ready to send</dt>
            <dd>Has a published version an envelope could name, including one scheduled but not yet in effect.</dd>
            <dt className="font-semibold text-stone-800">Version · Effective</dt>
            <dd>
              The version governing right now, computed from the chain on every read — no stored
              status. Dates are <b>UTC</b>, because an effective date is a legal calendar day, not
              an instant in your day.
            </dd>
            <dt className="font-semibold text-stone-800">Archiving</dt>
            <dd>
              Withdraws the template so no new envelope can name it. Changes nothing already
              signed, and is reversible from the Archive tab.
            </dd>
          </dl>
          <p className="mt-2.5 text-stone-500">
            Counts come from one page of{' '}
            <code className="rounded bg-stone-100 px-1 text-stone-700">GET /admin/envelopes</code>,
            newest first and filtered after the read, so on a busy account they under-report rather
            than over-report. A template&rsquo;s <b>Envelopes</b> tab has the rows behind a number.
          </p>
        </Explainer>
      </div>

      {menu && (
        <RowMenu
          at={menu}
          onClose={() => setMenu(null)}
          items={[
            { icon: FileSignature, label: 'Open template', onSelect: () => openRow(menu.doc, false) },
            { icon: ExternalLink, label: 'Open in new tab', onSelect: () => openRow(menu.doc, true) },
            { icon: Tags, label: 'Change category', onSelect: () => setMoving({ doc: menu.doc, mode: 'category' }) },
            { icon: FolderInput, label: 'Move to folder', onSelect: () => setMoving({ doc: menu.doc, mode: 'folder' }) },
            menu.doc.status === 'retired'
              ? { icon: ArchiveRestore, label: 'Restore from archive', onSelect: () => void restore(menu.doc) }
              : { icon: Archive, label: 'Archive template', danger: true, onSelect: () => setArchiving(menu.doc) },
          ]}
        />
      )}

      {moving && (
        <MoveDocumentModal
          document={moving.doc}
          folders={folders}
          kind="agreement"
          mode={moving.mode}
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); void refresh(); }}
        />
      )}

      {folderModal && (
        <FolderEditorModal
          folder={folderModal.folder}
          folders={folders}
          defaultParentId={selectedFolderId}
          onClose={() => setFolderModal(null)}
          onSaved={() => { setFolderModal(null); void load(); }}
        />
      )}

      {archiving && (
        <ArchiveModal
          document={archiving}
          candidates={active.filter((d) => d.documentId !== archiving.documentId)}
          onClose={() => setArchiving(null)}
          onArchived={() => { setArchiving(null); void refresh(); }}
        />
      )}

      {docModal && (
        <NewAgreementModal
          folders={folders}
          defaultFolderId={selectedFolderId}
          onClose={() => setDocModal(false)}
          onCreated={(documentId) => {
            setDocModal(false);
            navigate(`/legal/agreements/${encodeURIComponent(documentId)}`);
          }}
        />
      )}
    </StaffPage>
  );
}

/* ── pagination button ──────────────────────────────────────────────────── */

function PageButton({
  label, disabled, onClick, children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 bg-white text-stone-600 transition hover:bg-stone-100 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
    >
      {children}
    </button>
  );
}

/* ── row action menu ────────────────────────────────────────────────────── */

interface RowMenuItem {
  icon: FC<any>;
  label: string;
  danger?: boolean;
  onSelect: () => void;
}

/**
 * Drawn FIXED at the button's coordinates rather than absolutely inside the
 * cell, because the table scrolls horizontally inside `overflow-x-auto` and an
 * in-cell menu would be clipped by it — the last column is exactly where that
 * bites hardest.
 */
function RowMenu({
  at, items, onClose,
}: {
  at: { x: number; y: number };
  items: RowMenuItem[];
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const WIDTH = 224;
  // Clamped so a row near the right edge or the fold does not open a menu
  // half of which is off-screen.
  const left = Math.max(8, Math.min(at.x - WIDTH, window.innerWidth - WIDTH - 8));
  const top = Math.min(at.y + 4, Math.max(8, window.innerHeight - items.length * 40 - 24));

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        role="menu"
        style={{ position: 'fixed', left, top, width: WIDTH, zIndex: 50 }}
        className="rounded-xl border border-stone-200 bg-white p-1.5 shadow-xl"
      >
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            onClick={() => { onClose(); item.onSelect(); }}
            className={`flex min-h-[40px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[12.5px] ${
              item.danger
                ? 'text-red-600 hover:bg-red-50'
                : 'text-stone-700 hover:bg-stone-100 hover:text-stone-900'
            }`}
          >
            <item.icon size={15} className="shrink-0" />
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
}

/* ── archive ────────────────────────────────────────────────────────────── */

/**
 * ARCHIVING IS A WITHDRAWAL, NOT A DELETE — and this modal exists so it is
 * never mistaken for one.
 *
 * `POST .../retire` flips `status` and stamps `retiredAt`. Nothing is removed:
 * every version stays readable, every executed envelope stands, and the slug
 * stays permanently taken. What changes is that envelope creation refuses the
 * template (409 `document_retired`), so no NEW agreement can be sent from it.
 *
 * The optional superseding document is offered here rather than left to a
 * second trip: "we replaced this with that one" is the reason a template is
 * usually withdrawn, and recording it at the moment of withdrawal is the only
 * time anyone actually knows the answer.
 */
function ArchiveModal({
  document: doc, candidates, onClose, onArchived,
}: {
  document: LegalDocument;
  candidates: readonly LegalDocument[];
  onClose: () => void;
  onArchived: () => void;
}) {
  const [supersededBy, setSupersededBy] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await documentsApi.retireDocument(doc.documentId, supersededBy ?? undefined);
      toastService.success(res.alreadyRetired ? `${doc.title} was already archived` : `${doc.title} archived`);
      onArchived();
    } catch (e: any) {
      setError(e?.message || 'document_retire_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={<span className="flex items-center gap-2"><Archive size={17} className="text-red-600" />Archive template</span>}
      subtitle={doc.title}
      onClose={onClose}
      footerNote="Reversible — restore it from the Archive tab at any time."
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="danger" onClick={() => void submit()} disabled={saving}>
            {saving
              ? <span className="flex items-center gap-2"><RubyGemMark size={16} drawing color="currentColor" />Archiving…</span>
              : 'Archive template'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-[13px] leading-relaxed text-stone-600">
          Archiving withdraws this template. <b className="text-stone-800">No new envelope can be
          created from it</b> — the service refuses with <code className="rounded bg-stone-100 px-1">document_retired</code>.
        </p>
        <ul className="space-y-1.5 rounded-lg border border-stone-200 bg-stone-50 px-3.5 py-3 text-[12px] text-stone-600">
          <li>Nothing is deleted. Every version stays readable.</li>
          <li>Envelopes already sent or executed are untouched — a signature binds a version.</li>
          <li>The slug stays permanently taken, as it does for every document.</li>
        </ul>

        <div>
          <FieldLabel>Superseded by</FieldLabel>
          <SearchableDropdown
            options={candidates.map((c) => ({ value: c.documentId, label: c.title, subtitle: `/legal/${c.slug}` }))}
            value={supersededBy}
            onChange={setSupersededBy}
            placeholder="Optional — which template replaces this one?"
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Recorded on the document so anyone who finds this later is pointed at its
            replacement. Leave it blank if nothing replaces it.
          </p>
        </div>

        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}

/* ── new agreement ──────────────────────────────────────────────────────── */

/**
 * The same `POST /admin/documents` the Policies page calls, with `kind` pinned
 * to `agreement`. Kind is not offered as a choice here: the page you are on is
 * the choice, and kind is what decides whether the document is accepted or
 * signed — changing it later would change the document's legal meaning.
 */
function NewAgreementModal({
  folders, defaultFolderId, onClose, onCreated,
}: {
  folders: readonly Folder[];
  defaultFolderId: string | null;
  onClose: () => void;
  onCreated: (documentId: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [folderId, setFolderId] = useState<string | null>(defaultFolderId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(title);

  const problems: string[] = [];
  if (!title.trim()) problems.push('Give the agreement a title.');
  if (!folderId) problems.push('Choose a folder.');
  if (!effectiveSlug || !isValidSlug(effectiveSlug)) {
    problems.push('The slug must be lowercase words separated by single hyphens.');
  }

  /**
   * WHERE this document is offered. Optional at the API, asked here: an
   * uncategorised document appears in no picker, so leaving it blank is a way
   * to lose a document rather than a neutral choice.
   */
  const [category, setCategory] = useState<DocumentCategory | null>(null);
  // Live categories for this kind — the same list the table filters by, so a
  // category added at /legal/categories is offered here without a deploy.
  const { options: modalCategoryChoices } = useDocumentCategories('agreement');

  const submit = async () => {
    if (problems.length) return;
    setSaving(true);
    setError(null);
    try {
      const res = await documentsApi.createDocument({
        folderId: folderId!, slug: effectiveSlug, title: title.trim(), kind: 'agreement',
        ...(category ? { category } : {}),
      });
      toastService.success(`${res.document.title} created as v1 draft`);
      onCreated(res.document.documentId);
    } catch (e: any) {
      setError(
        e?.message === 'slug_already_exists'
          ? 'That slug is already taken. Slugs are permanent public URLs and are never reused, '
            + 'even after a document is retired.'
          : e?.message || 'document_create_failed',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="New agreement"
      subtitle="Created with an empty v1 draft, authored in the same editor as a policy. Nothing can be signed until a version is published."
      onClose={onClose}
      footerNote="The slug is permanent — it can never be changed or reused."
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={saving || problems.length > 0}
          >
            {saving ? 'Creating…' : 'Create agreement'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel requirement="required">Title</FieldLabel>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Device Rental Agreement"
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <FieldLabel requirement="required · permanent">Slug</FieldLabel>
          <input
            value={effectiveSlug}
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
            placeholder="device-rental-agreement"
            className={INPUT_CLASS}
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Public URL: <span className="text-stone-700">/legal/{effectiveSlug || '…'}</span>
          </p>
        </div>

        <div>
          <FieldLabel requirement="required">Folder</FieldLabel>
          <SearchableDropdown
            options={folders.map((f) => ({ value: f.folderId, label: f.name }))}
            value={folderId}
            onChange={setFolderId}
            placeholder={folders.length ? 'Choose a folder' : 'Create a folder first'}
            disabled={folders.length === 0}
          />
        </div>

        <div>
          <FieldLabel requirement="recommended">Category</FieldLabel>
          <SearchableDropdown
            options={modalCategoryChoices}
            value={category}
            onChange={(value) => setCategory(value as DocumentCategory | null)}
            placeholder="Where is this offered?"
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Decides where this appears — a device rental agreement is offered on the rent option, a
            software licence at checkout. Without one it stays in the library and shows up in no picker.
          </p>
        </div>

        <div>
          <FieldLabel>Kind</FieldLabel>
          <div className="rounded-lg border border-stone-300 bg-stone-50 px-3.5 py-3 text-[13px] text-stone-700">
            Agreement
            <span className="ml-2 text-[11.5px] text-stone-500">
              instantiated into an envelope and signed — not accepted
            </span>
          </div>
        </div>

        {problems.length > 0 && (title || slugTouched || folderId) && (
          <ul className="space-y-1 rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}
        {error && (
          <p className="rounded-lg border border-red-300 bg-red-50 px-3.5 py-3 text-[12px] text-red-700">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
